import React, { useState } from 'react';

// Confirmation modal for voiding a receipt (ASN receiving session or
// manifest receipt). Captures a reason code, an optional customer-facing
// reason, and staff-only internal notes. The heavy lifting (eligibility,
// reversal, billing) happens server-side; this just collects intent.
const REASON_OPTIONS = [
  { value: 'wrong_request', label: 'Received against the wrong request' },
  { value: 'wrong_item', label: 'Wrong item / SKU received' },
  { value: 'wrong_document', label: 'Received against the wrong document (ASN/manifest)' },
  { value: 'vendor_misship', label: 'Vendor mis-shipment' },
  { value: 'other', label: 'Other' }
];

const VoidReceiptModal = ({ isOpen, onClose, receiptLabel, unitCount, onConfirm, isLoading }) => {
  const [reasonCode, setReasonCode] = useState('wrong_request');
  const [customerReason, setCustomerReason] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm({
      reasonCode,
      customerReason: customerReason.trim() || null,
      internalNotes: internalNotes.trim() || null
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Void Receipt</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
            disabled={isLoading}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 text-sm text-red-800">
          <p className="font-medium mb-1">
            This reverses {receiptLabel}{unitCount ? ` (${unitCount} unit${unitCount === 1 ? '' : 's'})` : ''}.
          </p>
          <p>
            Received units return to pending, stock and storage billing are
            corrected, and any open invoice for the request is cancelled. This
            is blocked if any unit has been reserved or shipped.
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
          <select
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            disabled={isLoading}
          >
            {REASON_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Customer-facing reason <span className="text-gray-400 font-normal">(optional, visible to the customer)</span>
          </label>
          <textarea
            value={customerReason}
            onChange={(e) => setCustomerReason(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder="e.g. Items were received against the wrong order and have been corrected."
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            disabled={isLoading}
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Internal notes <span className="text-gray-400 font-normal">(staff only)</span>
          </label>
          <textarea
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            rows={2}
            maxLength={5000}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            disabled={isLoading}
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? 'Voiding...' : 'Void Receipt'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoidReceiptModal;
